import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Slider from 'react-slick';
import styled, { css } from 'styled-components';

import Fallback from '../fallback';
import FluidContainer from '../layouts/components/fluid-container';
import IconButton from '../icon-button';
import LeftArrow from '../navigation-bar/assets/leftChevron.svg';
import LightboxGallery from './lightbox-gallery';
import RightArrow from '../navigation-bar/assets/rightChevron.svg';
import ZoomIcon from './assets/magnfyingGlass.svg?react';
import { useWindowSize } from '../../lib/hooks';

export const PreviousArrow = styled.div`
    /* prettier-ignore */
    &:before {
        background: url("${LeftArrow}");
        background-repeat: no-repeat;
        content: '';
        display: block;
        height: 32px;
        width: 32px;
        background-size: 18px 18px;
    }
`;

export const NextArrow = styled.div`
    /* prettier-ignore */
    &:before {
        background: url("${RightArrow}");
        background-repeat: no-repeat;
        content: '';
        display: block;
        height: 32px;
        width: 32px;
        background-size: 18px 18px;
    }
`;

export const SliderContainer = styled.div`
    margin: 24px;
`;

export const Overlay = styled.div`
    display: none;
    background-color: #333333b3;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;

export const MainImageInfo = styled.div`
    border-radius: 4px;
    box-shadow: var(--shadow-6);
    position: relative;
    padding: ${(props) => (props.windowInnerWidth < 768 ? '0 8px 0 8px' : '0 16px 0 16px')};
    margin-bottom: ${(props) => props.singleImage && '24px'};

    &:hover > ${Overlay} {
        display: flex;
    }

    &:focus {
        box-shadow: var(--outline);
    }
`;

export const MainImageContainer = styled.div`
    width: 100%;
    border: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: ${(props) => (props.isWithCaption ? '308px' : '340px')};
    padding: ${(props) => (props.windowInnerWidth < 768 ? '8px 0 8px 0' : '16px 0 16px 0')};
    ${(props) =>
        props.isWithCaption &&
        css`
            padding-bottom: 0px;
        `}
    box-sizing: border-box;

    align-items: center;
    background-color: var(--color-white);

    &::-moz-focus-inner {
        border: 0;
    }
`;

export const MainImage = styled.img`
    display: block;
    max-width: 100%;
`;

const ImgContainer = styled.div`
    position: relative;
    width: 100%;
    padding-top: 100%;
    border-radius: 4px;
    box-shadow: var(--shadow-2);
    cursor: pointer;

    &:focus {
        outline: none;
    }

    &:hover {
        box-shadow: var(--shadow-4);
    }

    &:focus {
        box-shadow: 0px 0px 2px 2px var(--blue200);
    }

    ${(props) =>
        props.isSelected &&
        css`
            box-shadow: var(--shadow-1);
        `}
`;

export const ImgThumbnail = styled.div`
    position: absolute;
    top: 2px;
    bottom: 2px;
    left: 2px;
    right: 2px;
    border-radius: 4px;

    cursor: pointer;

    img {
        object-fit: cover;
    }
    outline: none;

    ${(props) =>
        props.isSelected &&
        css`
            opacity: 70%;
        `}
`;

export const Caption = styled.div`
    align-self: flex-start;
    display: flex;
    align-items: center;
    font-size: 14px;
    height: ${(props) => (props.windowInnerWidth < 768 ? '24px' : '32px')};
    font-weight: 600;
    letter-spacing: 0.32px;
    line-height: 16px;
`;

const ImageGallery = ({ images = [] }) => {
    const windowSize = useWindowSize();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showLightboxGallery, setShowLightboxGallery] = useState(false);
    const [error, setError] = useState(false);

    const settings = {
        slidesToShow: 3,
        slideToScroll: 1,
        infinite: false,
        arrows: true,
        prevArrow: <PreviousArrow />,
        nextArrow: <NextArrow />,
    };

    const windowInnerWidth = windowSize.innerWidth;
    const image = images[currentImageIndex];
    const size = images.length;

    const openLightboxGallery = () => {
        setShowLightboxGallery(true);
    };

    const closeLightboxGallery = () => {
        setShowLightboxGallery(false);
    };

    useEffect(() => {
        setError(false);
    }, [image]);

    return (
        <>
            {!error && (
                <>
                    <MainImageInfo windowInnerWidth={windowInnerWidth} singleImage={size === 1}>
                        <MainImageContainer windowInnerWidth={windowInnerWidth} isWithCaption={!!image.caption}>
                            <MainImage src={image.src} alt={image.alt} onError={() => setError(true)} />
                        </MainImageContainer>
                        {image.caption && <Caption windowInnerWidth={windowInnerWidth}>{image.caption}</Caption>}

                        <Overlay onClick={openLightboxGallery}>
                            <IconButton type="secondary">
                                <ZoomIcon />
                            </IconButton>
                        </Overlay>
                    </MainImageInfo>
                    {showLightboxGallery && <LightboxGallery images={images} index={currentImageIndex} onClose={closeLightboxGallery} />}
                </>
            )}

            {error && (
                <Fallback
                    as="img"
                    src={image.src}
                    height={448}
                    width="100%"
                    flexDirection="column"
                    justifyContent="center"
                    render={() => (
                        <>
                            {"C'è un problema con l’immagine."}
                            <br />
                            {'Ricarica la pagina o contattaci.'}
                        </>
                    )}
                    alt={image.alt}
                />
            )}
            {size > 1 && (
                <SliderContainer>
                    <Slider {...settings}>
                        {images.map((image, index) => (
                            <FluidContainer key={index} padding={['xs', 'small']}>
                                <ImgContainer key={index} isSelected={currentImageIndex === index}>
                                    <ImgThumbnail isSelected={currentImageIndex === index}>
                                        <Fallback
                                            as="img"
                                            src={image.src}
                                            showIcon={false}
                                            flexDirection="column"
                                            justifyContent="center"
                                            padding={[0, 'small']}
                                            width="100%"
                                            height="100%"
                                            fontSize="12px"
                                            text="C'è un problema con l’immagine."
                                            tabIndex={0}
                                            onClick={() => setCurrentImageIndex(index)}
                                        />
                                    </ImgThumbnail>
                                </ImgContainer>
                            </FluidContainer>
                        ))}
                    </Slider>
                </SliderContainer>
            )}
        </>
    );
};

ImageGallery.propTypes = {
    images: PropTypes.array.isRequired,
};

export default ImageGallery;
